import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CreateNewNodesService } from '../../services/create-new-nodes.service';
import { NodeFactory } from '@modules/campaigns/Domain/Factories/NodeFactory';
import { WorkflowClass } from '@modules/campaigns/Domain/WorkflowClass';
import { draggableMenu, draggables } from '@shared/components/joint-js/config/constants';
import { CollectorChannelService } from '@shared/services/channelConfigCollectors.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';

@Component({
  selector: 'app-node-drag-drop',
  templateUrl: './node-drag-drop.component.html',
  styleUrls: ['./node-drag-drop.component.scss'],
})
export class NodeDragDropComponent implements OnInit {
  @Input() graph: any;
  @Input() workflow: WorkflowClass | undefined;
  @Output() nodeDrag = new EventEmitter<any>();

  draggables: draggableMenu = draggables;
  showWarningModal = false;
  currentChannel = '';
  warningType: 'channel' | 'waitNode' | null = null;

  constructor(
    private createNewNodes: CreateNewNodesService,
    private collectorChannel: CollectorChannelService,
  ) {}

  ngOnInit() {
    this.draggables = this.createNewNodes.translateDraggables(draggables);

    this.collectorChannel.loadData().subscribe({
      error: (err) => {
        console.error('Failed to load collector data', err);
      },
    });
  }

  stringToNodeType(typeStr: string): NodeType | undefined {
    return Object.values(NodeType).find((t) => t === typeStr);
  }

  drop(event: CdkDragDrop<string[]>) {
    const type = event.item.element.nativeElement.id;
    const typeNode = this.stringToNodeType(type);
    const channel = this.isChannelDisabled(type);

    if (channel) {
      this.warningType = 'channel';
      this.showWarningModal = true;
      this.currentChannel = type;
    }

    if (!this.workflow!.canAddWaitTypeNode(typeNode!)) {
      this.warningType = 'waitNode';
      this.showWarningModal = true;
      return;
    }

    if (!type) {
      this.handleError('Node type is not defined.');
      return;
    }

    const localPoint = this.graph.paper!.clientToLocalPoint(event.dropPoint.x, event.dropPoint.y);

    try {
      const newNode = this.createNewNodes.createNode(type, localPoint.x, localPoint.y);
      if (!newNode) {
        this.handleError('Failed to create a new node.');
        return;
      }

      // DOMAIN LOGIC
      const newDNodeID = newNode?.attributes?.['data']?.original;
      const nodeType = NodeFactory.mapStringToNodeType(type);
      if (nodeType && newDNodeID) {
        const newDNode = NodeFactory.createNode(
          nodeType,
          localPoint.x,
          localPoint.y,
          newDNodeID.toString(),
        );

        if (newDNode && this.workflow) {
          newDNode.initNodeConfig();
          this.workflow.addNode(newDNode);
        } else {
          this.handleError('Error: Could not add node to workflow.');
        }
      } else {
        this.handleError('Error: Node type or ID is missing.');
      }

      this.graph.graph!.addCell(newNode);
      this.graph.selectedElement = newNode;
      this.nodeDrag.emit(newNode);
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.handleError('An unexpected error occurred: ' + error.message);
      } else {
        this.handleError('An unexpected error occurred: ' + String(error));
      }
    }
  }

  private handleError(message: string) {
    console.error(message);
  }

  isChannelDisabled(channel: string): boolean {
    const notFullyUsedChannels = this.collectorChannel.getChannelsNotFullyUsed();
    const normalizedChannel = this.normalizeChannelKey(channel);
    return notFullyUsedChannels.includes(normalizedChannel);
  }

  normalizeChannelKey(channel: string): string {
    const noSuper = channel.replace(/^super/, '');
    return noSuper.charAt(0).toLowerCase() + noSuper.slice(1);
  }
}
